import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
/**
 * Message input (from the performance cockpit's ChatComposer): growing
 * textarea, Enter sends, Shift+Enter newline, round send button, red stop
 * button while an answer streams. With onAddFiles also images: paperclip
 * (file picker), camera on touch devices, paste and drag and drop, with a
 * thumbnail row above the text.
 */
import { useCallback, useEffect, useImperativeHandle, useRef, useState, } from "react";
import { useCameraMode } from "../camera.js";
import { CameraCapture } from "../CameraCapture.js";
import { Icon } from "../Icon.js";
import { ProgressRing } from "../ProgressRing.js";
import { fill, useTexts } from "../texts.js";
import { Spinner } from "../ui.js";
import { cx } from "../util.js";
const hasFiles = (e) => Array.from(e.dataTransfer?.types ?? []).includes("Files");
const imagesOf = (files) => Array.from(files ?? []).filter((f) => f.type.startsWith("image/"));
function Thumbnail({ a, onRemove, }) {
    const t = useTexts();
    const busy = a.status === "preparing" || a.status === "uploading";
    const measured = a.status === "uploading" && a.progress !== undefined;
    const label = a.status === "error"
        ? a.error
        : a.status === "preparing"
            ? t.assistantPreparing
            : measured
                ? fill(t.assistantUploadProgress, { percent: Math.round((a.progress ?? 0) * 100) })
                : a.status === "uploading"
                    ? t.assistantUploading
                    : a.name;
    return (_jsxs("li", { className: "relative h-14 w-14 shrink-0", title: label, children: [_jsxs("div", { className: cx("flex h-14 w-14 items-center justify-center overflow-hidden rounded-xl bg-surface ring-1", a.status === "error" ? "ring-err/60" : "ring-line"), children: [a.previewUrl ? (_jsx("img", { src: a.previewUrl, alt: a.name || t.assistantImage, className: cx("h-full w-full object-cover", busy && "opacity-60") })) : (_jsx(Icon, { name: "image", size: 20, className: "text-muted" })), busy && (_jsx("span", { className: "absolute inset-0 flex items-center justify-center text-accent", role: "status", "aria-label": label, children: measured ? _jsx(ProgressRing, { value: a.progress ?? 0, size: 30, stroke: 4 }) : _jsx(Spinner, { size: 16 }) })), a.status === "error" && (_jsx("span", { className: "absolute inset-0 flex items-center justify-center bg-err-subtle/80", role: "img", "aria-label": label, children: _jsx(Icon, { name: "alert", size: 18, className: "text-err-fg" }) }))] }), onRemove && a.status !== "uploading" && (_jsx("button", { type: "button", onClick: () => onRemove(a.id), className: "absolute -top-1.5 -right-1.5 flex h-5 w-5 items-center justify-center rounded-full bg-surface text-fg-2 shadow ring-1 ring-line transition-colors hover:bg-hover hover:text-fg pointer-coarse:-top-2.5 pointer-coarse:-right-2.5 pointer-coarse:h-7 pointer-coarse:w-7 after:absolute after:-inset-2 after:content-['']", "aria-label": `${t.assistantRemoveAttachment}: ${a.name || t.assistantImage}`, title: t.assistantRemoveAttachment, children: _jsx(Icon, { name: "x", size: 11, strokeWidth: 2.6 }) }))] }));
}
export function ChatComposer({ onSend, onCancel, isStreaming, disabled, placeholder, ref, attachments, onAddFiles, onRemoveAttachment, accept, allowEmpty, }) {
    const t = useTexts();
    const ta = useRef(null);
    const picker = useRef(null);
    const camera = useRef(null);
    // 0.10.2: the native camera app on iOS and Android, the CameraCapture
    // dialog on every other device with a camera (Windows tablets, webcams)
    const cameraMode = useCameraMode();
    const [shooting, setShooting] = useState(false);
    const [dragging, setDragging] = useState(false);
    const adjustHeight = useCallback(() => {
        const el = ta.current;
        if (!el)
            return;
        el.style.height = "auto";
        el.style.height = `${Math.min(el.scrollHeight, 140)}px`;
    }, []);
    useEffect(() => adjustHeight(), [adjustHeight]);
    useImperativeHandle(ref, () => ({
        focus: () => ta.current?.focus(),
        setText: (s) => {
            if (!ta.current)
                return;
            ta.current.value = s;
            adjustHeight();
            ta.current.focus();
        },
    }));
    const submit = () => {
        const el = ta.current;
        if (!el)
            return;
        const text = el.value.trim();
        if ((!text && !allowEmpty) || isStreaming || disabled)
            return;
        const r = onSend(text);
        el.value = "";
        adjustHeight();
        if (r && typeof r === "object" && "then" in r) {
            void r.then((ok) => {
                // not sent (an image upload failed): give the text back unless the user typed again
                if (ok === false && ta.current && !ta.current.value && text) {
                    ta.current.value = text;
                    adjustHeight();
                }
            });
        }
    };
    const sendButton = isStreaming ? (_jsx("button", { type: "button", onClick: onCancel, className: "flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-red-500 text-white transition-colors hover:bg-red-600 pointer-coarse:h-11 pointer-coarse:w-11", title: t.assistantStop, "aria-label": t.assistantStop, children: _jsx(Icon, { name: "stop", size: 14, strokeWidth: 0, fill: "currentColor" }) })) : (_jsx("button", { type: "button", onClick: submit, disabled: disabled, className: cx("flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-brand text-on-brand transition-colors hover:bg-brand-bright pointer-coarse:h-11 pointer-coarse:w-11", "disabled:cursor-not-allowed disabled:opacity-40"), title: t.assistantSendTitle, "aria-label": t.assistantSend, children: _jsx(Icon, { name: "send", size: 14 }) }));
    const textarea = (_jsx("textarea", { ref: ta, placeholder: placeholder ?? t.assistantPlaceholder, "aria-label": t.assistantMessageLabel, onKeyDown: (e) => {
            if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                submit();
            }
        }, onPaste: onAddFiles
            ? (e) => {
                const files = imagesOf(e.clipboardData?.files);
                if (files.length === 0)
                    return; // text paste stays as it is
                e.preventDefault();
                onAddFiles(files);
            }
            : undefined, onInput: adjustHeight, disabled: disabled, rows: 1, className: "max-h-[140px] min-h-[36px] flex-1 resize-none bg-transparent py-1.5 text-sm leading-relaxed text-fg outline-none placeholder:text-muted focus-visible:outline-none disabled:opacity-50" }));
    if (!onAddFiles) {
        return (_jsxs("div", { className: "mx-3 mt-2.5 mb-1.5 flex items-end gap-2 rounded-2xl bg-surface-2 px-3 py-1.5 ring-1 ring-line focus-within:ring-2 focus-within:ring-accent", children: [textarea, sendButton] }));
    }
    const add = (files) => {
        const list = Array.from(files ?? []);
        if (list.length)
            onAddFiles(list);
    };
    const toolButton = "flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full text-fg-3 transition-colors hover:bg-hover hover:text-fg disabled:cursor-not-allowed disabled:opacity-40 pointer-coarse:h-11 pointer-coarse:w-11";
    return (_jsxs("div", { className: cx("relative mx-3 mt-2.5 mb-1.5 flex flex-col gap-1.5 rounded-2xl bg-surface-2 px-3 py-1.5 ring-1 ring-line focus-within:ring-2 focus-within:ring-accent", dragging && "ring-2 ring-accent"), onDragEnter: (e) => {
            if (!hasFiles(e))
                return;
            e.preventDefault();
            setDragging(true);
        }, onDragOver: (e) => {
            if (!hasFiles(e))
                return;
            e.preventDefault();
            e.dataTransfer.dropEffect = "copy";
        }, onDragLeave: (e) => {
            if (e.currentTarget.contains(e.relatedTarget))
                return;
            setDragging(false);
        }, onDrop: (e) => {
            if (!hasFiles(e))
                return;
            e.preventDefault();
            setDragging(false);
            const files = imagesOf(e.dataTransfer.files);
            if (files.length)
                onAddFiles(files);
        }, children: [dragging && (_jsxs("div", { className: "pointer-events-none absolute inset-0 z-10 flex items-center justify-center rounded-2xl bg-surface-2/90 text-sm font-medium text-accent", children: [_jsx(Icon, { name: "image", size: 16, className: "mr-1.5" }), " ", t.assistantDropImages] })), attachments && attachments.length > 0 && (_jsx("ul", { className: "flex flex-wrap gap-2 pt-1.5", "aria-label": t.assistantAttach, children: attachments.map((a) => (_jsx(Thumbnail, { a: a, onRemove: onRemoveAttachment }, a.id))) })), _jsxs("div", { className: "flex items-end gap-1", children: [_jsx("button", { type: "button", className: toolButton, onClick: () => picker.current?.click(), disabled: disabled || isStreaming, title: t.assistantAttach, "aria-label": t.assistantAttach, children: _jsx(Icon, { name: "paperclip", size: 16 }) }), _jsx("input", { ref: picker, type: "file", accept: accept ?? "image/*", multiple: true, hidden: true, tabIndex: -1, onChange: (e) => {
                            add(e.currentTarget.files);
                            e.currentTarget.value = "";
                        } }), cameraMode !== "none" && (_jsxs(_Fragment, { children: [_jsx("button", { type: "button", className: toolButton, onClick: () => (cameraMode === "native" ? camera.current?.click() : setShooting(true)), disabled: disabled || isStreaming, title: t.assistantCamera, "aria-label": t.assistantCamera, children: _jsx(Icon, { name: "camera", size: 16 }) }), _jsx("input", { ref: camera, type: "file", accept: "image/*", capture: "environment", hidden: true, tabIndex: -1, onChange: (e) => {
                                    add(e.currentTarget.files);
                                    e.currentTarget.value = "";
                                } }), cameraMode === "stream" && (_jsx(CameraCapture, { open: shooting, onClose: () => setShooting(false), onCapture: (f) => add([f]), onFallback: () => picker.current?.click() }))] })), _jsx("div", { className: "ml-1 flex min-w-0 flex-1", children: textarea }), sendButton] })] }));
}
